import { maplibreGL } from '@maplibre/maplibre-gl-leaflet'
import 'maplibre-gl/dist/maplibre-gl.css'
import PropTypes from 'prop-types'
import { Component } from 'react'

/*
 * Minimal structural types instead of importing from 'leaflet'.
 *
 * This project has `allowJs` on and the `leaflet` package ships no `types` field,
 * so TypeScript infers namespaces from leaflet's JS rather than picking up
 * @types/leaflet — `import type { Map } from 'leaflet'` fails with "Cannot use
 * namespace 'Map' as a type". The rest of the app works around this with `any`
 * (see `mapRef` in update-address-map.tsx); these two shapes are all this
 * component touches, so they keep it type-safe without that.
 */
type LeafletLayerLike = { addTo(map: LeafletMapLike): unknown }
type LeafletMapLike = { removeLayer(layer: LeafletLayerLike): unknown }

type Props = {
  /**
   * MapLibre style JSON URL — Quiqup's own basemap on tiles.quiqup.app. The style
   * is a mutable pointer at an immutable, versioned tile archive, so the basemap
   * can be rebuilt or rolled back without deploying this app.
   */
  styleUrl: string
}

/**
 * Quiqup's vector basemap, as a child of any react-leaflet v1 `<Map>`.
 *
 * Replaces the MapTiler raster `<TileLayer>`. The GL layer is added to the map's
 * tile pane, underneath every Leaflet overlay, so markers, routes, GeoJSON and
 * controls are untouched — this is a base-layer swap, not a move to a different
 * map library.
 *
 * It reads the map off react-leaflet v1's legacy context rather than taking it as
 * a prop, which is what lets the same component drop into the SDK's `<QuMap>` (it
 * renders `{this.props.children}` inside its own `<Map>`) as well as the two maps
 * this app builds itself. Legacy context is how react-leaflet 1.x passes the map
 * to every layer — see `MapLayer.contextTypes` — so this is the idiomatic seam,
 * not a workaround.
 *
 * Attribution is left to MapLibre's own control, which reads it from the style
 * JSON and merges into Leaflet's attribution bar. OSM's ODbL and OpenMapTiles'
 * CC-BY both require the credit, and sourcing it from the style means it cannot
 * drift from whatever the basemap actually serves.
 *
 * NOTE ON maplibre-gl VERSION — keep it on 5.x. maplibre-gl-leaflet@0.1.4 declares
 * `^6.0.0` in its peer range but does not work with v6: it reaches into the GL
 * map's internal `transform` to stay in sync with Leaflet, and v6 restructured it.
 * The failure is silent — style and sprite load fine, no console error, but the map
 * never works out which tiles it needs and renders an empty canvas.
 */
export default class VectorBasemap extends Component<Props> {
  static contextTypes = {
    map: PropTypes.object,
  }

  private layer?: LeafletLayerLike

  /**
   * react-leaflet v1 passes the map down via legacy context; see the class docs.
   * Read through a cast rather than a `declare context` field — CRA's Babel
   * transform rejects TypeScript `declare` fields, and a plain typed field would
   * emit a class property that shadows React's own `context`.
   */
  private get map(): LeafletMapLike | undefined {
    return (this.context as { map?: LeafletMapLike } | undefined)?.map
  }

  componentDidMount() {
    const map = this.map
    if (!map || !this.props.styleUrl) {
      // No map yet, or no style configured for this environment — render nothing
      // rather than initialising a GL map against an empty style URL.
      return
    }

    this.layer = maplibreGL({ style: this.props.styleUrl }) as unknown as LeafletLayerLike
    this.layer.addTo(map)
  }

  componentWillUnmount() {
    const map = this.map
    if (map && this.layer) {
      map.removeLayer(this.layer)
    }
  }

  render() {
    return null
  }
}
